/* ON:PACE product page — live shipping quote.
 *
 * Same job as the store's co-ship-calc.js and the same Shopify endpoints, but
 * NOT that file. Two reasons it is separate rather than reused:
 *
 * 1. THE RESTORE. To quote a rate Shopify needs the item in the cart, so this
 *    adds one, quotes, and takes it back out. The shared script takes it out
 *    with /cart/change.js keyed on the VARIANT id, which resolves to the FIRST
 *    line holding that variant. On this page there is very often already a
 *    line holding that variant -- the STOQ deposit -- and a selling plan makes
 *    a separate line, so the shared script would restore the DEPOSIT line and
 *    leave the full-price one it just added sitting in the customer's cart.
 *    This one keys the restore on the LINE KEY returned by /cart/add.js, which
 *    can only ever be the line it created, and it remembers the pre-existing
 *    quantity of that exact line so a customer who already had one keeps it.
 *
 * 2. THE STYLESHEET. The shared snippet pulls co-ship-calc.css into the body,
 *    i.e. after onpace.css, which would hand every specificity tie to the
 *    retailer's styling. Nothing here loads it; the whole component is styled
 *    from onpace.css.
 *
 * NOTHING here is named cosc__. co-ship-calc.css reaches this page anyway --
 * the cart drawer pulls it into the BODY, after onpace.css -- so any class
 * shared with the store's calculator would lose every specificity tie to a
 * stylesheet we are not loading and did not intend to consult.
 */
(function () {
  'use strict';

  var POLL_MS = 700;
  var POLL_MAX = 14;            // ~10s; carrier apps are the slow part

  function req(url, opts) {
    opts = opts || {};
    opts.headers = Object.assign({ 'Accept': 'application/json' }, opts.headers || {});
    return fetch(url, opts).then(function (r) {
      return r.json().catch(function () { return {}; })
        .then(function (data) { return { status: r.status, ok: r.ok, data: data }; });
    });
  }

  /* /cart/async_shipping_rates.json returns `price` as a string in DOLLARS
     ("124.50"), not cents. This divided by 100 and quoted the SOYO 5 at $1.25
     to Melbourne -- invisible for as long as ON:PACE returned no rates at all,
     and live the moment the delivery profile started working (2026-09-10).
     The store's co-ship-calc.js never divided, and its figures agree with the
     Storefront API to the cent, which is what settles the unit. */
  function money(v) {
    var n = parseFloat(v);
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Read the live variant off the buy form so the quote follows the variant
     picker, and fall back to the value rendered into the element. */
  function variantId(root) {
    var input = document.querySelector('#op-form [name="id"]') ||
                document.querySelector('form[action*="/cart/add"] [name="id"]');
    if (input && input.value) return input.value;
    return root.getAttribute('data-variant-id');
  }

  function poll(query, n) {
    return req('/cart/async_shipping_rates.json?' + query).then(function (res) {
      if (res.status === 200 && res.data && res.data.shipping_rates) return res.data.shipping_rates;
      if (n <= 0) throw new Error('timeout');
      return new Promise(function (r) { setTimeout(r, POLL_MS); })
        .then(function () { return poll(query, n - 1); });
    });
  }

  function render(el, rates, zip) {
    if (!rates || !rates.length) {
      el.className = 'op-ship__res is-empty';
      el.textContent = 'No delivery options came back for ' + zip +
        '. Check the postcode, or ask us and we will quote it by hand.';
      return;
    }
    rates = rates.slice().sort(function (a, b) { return parseFloat(a.price) - parseFloat(b.price); });
    var html = '<p class="op-ship__rhead">Delivery to ' + esc(zip) + '</p><ul class="op-ship__rates">';
    rates.forEach(function (r) {
      var p = parseFloat(r.price);
      html += '<li class="op-ship__rate"><span class="op-ship__rname">' +
              esc(r.presentment_name || r.name || 'Delivery') + '</span>' +
              '<span class="op-ship__rprice' + (p === 0 ? ' is-free' : '') + '">' +
              (p === 0 ? 'Free' : money(r.price)) + '</span></li>';
    });
    el.className = 'op-ship__res is-success';
    el.innerHTML = html + '</ul>';
  }

  function calculate(root) {
    var out = root.querySelector('[data-op-ship-result]');
    var btn = root.querySelector('[data-op-ship-calc]');
    var state = (root.querySelector('[data-op-ship-state]') || {}).value || '';
    var zipEl = root.querySelector('[data-op-ship-zip]');
    var cityEl = root.querySelector('[data-op-ship-city]');
    var zip = zipEl ? zipEl.value.trim() : '';
    var city = cityEl ? cityEl.value.trim() : '';

    function fail(msg) { out.className = 'op-ship__res is-error'; out.textContent = msg; }

    if (!state.trim() || !zip) return fail('Choose your state and enter a postcode.');
    if (!/^\d{3,4}$/.test(zip)) return fail('That does not look like an Australian postcode.');
    if (cityEl && !city) return fail('Enter your suburb.');

    var addr = new URLSearchParams();
    addr.set('shipping_address[zip]', zip);
    addr.set('shipping_address[country]', 'Australia');
    addr.set('shipping_address[province]', state.trim());
    if (city) addr.set('shipping_address[city]', city);
    var query = addr.toString();

    var id = variantId(root);
    if (!id) return fail('We could not read the model, so the quote would be for the wrong machine. Reload the page and try again.');

    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Checking…';
    out.className = 'op-ship__res is-loading';
    out.innerHTML = '<span class="op-ship__spin" aria-hidden="true"></span> Getting live rates to ' + esc(zip) + '…';

    var key = null, was = 0;

    /* The quantity of the line WE are about to touch -- matched on variant AND
       the absence of a selling plan, so a deposit line is never mistaken for
       ours. If /cart/add.js merges into an existing plain line, this is what it
       gets put back to; if it creates one, this is 0 and the line goes. */
    req('/cart.js')
      .then(function (res) {
        (res.data.items || []).forEach(function (it) {
          if (String(it.id) === String(id) && !it.selling_plan_allocation) was = it.quantity;
        });
        return req('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id, quantity: 1 })
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error(res.data && res.data.description || 'add failed');
        key = res.data.key;
        return req('/cart/prepare_shipping_rates.json?' + query, { method: 'POST' });
      })
      .then(function () { return poll(query, POLL_MAX); })
      .then(function (rates) {
        return restore().then(function () { render(out, rates, zip); });
      })
      .catch(function (e) {
        restore();
        fail(e && e.message === 'timeout'
          ? 'The carrier did not answer in time. Try again in a moment.'
          : 'We could not get a rate just now. Try again, or ask us and we will quote it by hand.');
      })
      .then(function () { btn.disabled = false; btn.textContent = label; });

    function restore() {
      if (!key) return Promise.resolve();
      var k = key;
      key = null;
      return req('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: k, quantity: was })
      }).catch(function () {});
    }
  }

  /* Delegated, so a section re-render in the theme editor needs no rebinding. */
  document.addEventListener('click', function (e) {
    var t = e.target.closest('[data-op-ship-toggle]');
    if (t) {
      var root = t.closest('[data-op-ship]');
      var panel = root.querySelector('[data-op-ship-panel]');
      var open = root.classList.toggle('is-open');
      t.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
      return;
    }
    var c = e.target.closest('[data-op-ship-calc]');
    if (c) calculate(c.closest('[data-op-ship]'));
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter') return;
    var f = e.target.closest('[data-op-ship-zip], [data-op-ship-city]');
    if (!f) return;
    e.preventDefault();
    calculate(f.closest('[data-op-ship]'));
  });
})();
